/**
 * Шифрування вмісту записів ключем пристрою. [ADR-0015]
 *
 * Ключ створюється невитягуваним (`extractable: false`) і живе в IndexedDB
 * як `CryptoKey`. Це не формальність: браузер відмовляє `exportKey`, тож
 * ключ ніколи не існує в JavaScript як байти. Скрипт на сторінці може
 * попросити розшифрувати конкретний запис, але не може винести ключ, щоб
 * потім розшифрувати ним украдену копію бази десь окремо.
 *
 * Чого це **не** робить, і це варто знати:
 * - не рятує від коду, який виконується на цьому ж походженні: він просто
 *   попросить розшифрувати;
 * - не шифрує назви записів, дати, теги й пошуковий індекс — вони лишаються
 *   відкритими, бо за ними шукають і сортують ([ADR-0012], [ADR-0014]);
 * - очищення даних сайту знищує ключ, а з ним і всі записи. Саме тому
 *   експорт архіву — не пункт списку, а обіцянка.
 *
 * Алгоритм AES-256-GCM, а не XChaCha20-Poly1305 із docs/05: WebCrypto вміє
 * невитягувані ключі, а стороння бібліотека — ні, бо мусить бачити байти.
 * Ідентифікатор алгоритму в конверті це розрізняє.
 */
import {
  Algorithm,
  type CipherPort,
  DecryptionFailedError,
  decodeEnvelope,
  encodeEnvelope,
} from "@beresta/core";
import { KEY_STORE, idbRequest, openKeyDb } from "./idb.js";

const KEY_ID = "device";
const NONCE_BYTES = 12;

/**
 * Копія байтів у власний буфер.
 *
 * WebCrypto приймає лише `ArrayBuffer`, а наші масиви типізовані як
 * `ArrayBufferLike` — теоретично це може бути `SharedArrayBuffer`, і
 * TypeScript має рацію, що не пропускає. Крім типів, є й суть: `decodeEnvelope`
 * віддає підмасиви-види на спільний буфер, і передавати їх кудись назовні
 * означає ділитися більшим, ніж збиралися.
 */
function ownBytes(view: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(view.length);
  out.set(view);
  return out;
}

/**
 * Ключ пристрою: читаємо наявний або створюємо один раз.
 *
 * Гонка тут реальна — два одночасні старти могли б створити два ключі,
 * і другий зробив би всі попередні записи нечитними. Тому після запису
 * читаємо те, що справді лежить у сховищі, а не те, що ми поклали.
 */
async function deviceKey(): Promise<CryptoKey> {
  const db = await openKeyDb();
  const get = () =>
    idbRequest<CryptoKey | undefined>(db, KEY_STORE, "readonly", (s) => s.get(KEY_ID));
  const existing = await get();
  if (existing) return existing;

  const fresh = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, [
    "encrypt",
    "decrypt",
  ]);
  await idbRequest(db, KEY_STORE, "readwrite", (s) => s.add(fresh, KEY_ID)).catch(
    // `add` падає, якщо ключ уже зʼявився між читанням і записом. Це не
    // помилка: значить, інший старт нас випередив, і його ключ правильний.
    () => undefined,
  );
  const stored = await get();
  if (!stored) throw new Error("ключ пристрою не зберігся");
  return stored;
}

export async function createCipher(): Promise<CipherPort> {
  const key = await deviceKey();

  return {
    async seal(plaintext) {
      const nonce = crypto.getRandomValues(new Uint8Array(NONCE_BYTES));
      const ciphertext = new Uint8Array(
        await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce }, key, ownBytes(plaintext)),
      );
      return encodeEnvelope({ algorithm: Algorithm.AesGcm256, nonce, ciphertext });
    },

    async open(sealed) {
      const envelope = decodeEnvelope(sealed);
      if (envelope.algorithm !== Algorithm.AesGcm256) {
        throw new DecryptionFailedError(`алгоритм ${envelope.algorithm} тут не розшифрувати`);
      }
      try {
        const plain = await crypto.subtle.decrypt(
          { name: "AES-GCM", iv: ownBytes(envelope.nonce) },
          key,
          ownBytes(envelope.ciphertext),
        );
        return new Uint8Array(plain);
      } catch {
        // GCM не розрізняє «не той ключ» і «змінені байти» — і це правильно.
        // Тут важливо не вдавати, що причина відома.
        throw new DecryptionFailedError("не той ключ або пошкоджені дані");
      }
    },
  };
}
